import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import s from "./SectionPicker.module.css";

/**
 * Click a button → modal opens → sections are shown grouped by grade level.
 * Typeable search filters across both grade and section name.
 *
 * Props:
 *   value         number|null     — currently selected section id
 *   onChange      fn(id)          — called with the new section id (or null on clear)
 *   sections      Array<{id,name,gradeLevelId}>
 *   gradeLevels   Array<{id,name}>
 *   required      bool            — hides the "Clear" button when true
 *   placeholder   string
 */
export default function SectionPicker({ value, onChange, sections, gradeLevels, required, placeholder = "— pick a section —" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => { if (!open) setQuery(""); }, [open]);

  // Escape closes the picker (outside-click no longer does).
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const sectionsById    = useMemo(() => Object.fromEntries((sections || []).map(r => [r.id, r])), [sections]);
  const gradeLevelsById = useMemo(() => Object.fromEntries((gradeLevels || []).map(r => [r.id, r])), [gradeLevels]);

  const labelFor = (sec) => {
    if (!sec) return null;
    const grade = gradeLevelsById[sec.gradeLevelId];
    return grade ? `${grade.name} · ${sec.name}` : sec.name;
  };

  const currentLabel = value ? labelFor(sectionsById[value]) : null;

  // Group sections by grade level, ordered by the grade level list (so Grade 1 → Grade 12).
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map();
    for (const sec of sections || []) {
      const grade = gradeLevelsById[sec.gradeLevelId];
      const gradeName = grade?.name || "Unassigned";
      const combined = `${gradeName} ${sec.name}`.toLowerCase();
      if (q && !combined.includes(q)) continue;
      if (!map.has(sec.gradeLevelId)) map.set(sec.gradeLevelId, { grade, sections: [] });
      map.get(sec.gradeLevelId).sections.push(sec);
    }
    // Preserve grade order
    const ordered = [];
    (gradeLevels || []).forEach(g => {
      if (map.has(g.id)) ordered.push(map.get(g.id));
    });
    // Include any "Unassigned" (sections with a missing gradeLevelId lookup) at the end
    for (const [gid, group] of map.entries()) {
      if (!gradeLevelsById[gid]) ordered.push(group);
    }
    return ordered;
  }, [sections, gradeLevels, gradeLevelsById, query]);

  const totalMatching = grouped.reduce((n, g) => n + g.sections.length, 0);

  function pick(id) {
    onChange(id);
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        className={s.trigger}
        onClick={() => setOpen(true)}>
        <span className={currentLabel ? "" : s.triggerPlaceholder}>
          {currentLabel || placeholder}
        </span>
        <span className={s.triggerCaret}>▾</span>
      </button>

      {open && createPortal(
        <div className={s.backdrop}>
          <div className={s.modal} onClick={e => e.stopPropagation()}>
            <div className={s.head}>
              <h3 className={s.title}>Pick a section</h3>
              <input
                className={s.search}
                autoFocus
                placeholder="Search grade or section name…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>

            <div className={s.body}>
              {grouped.length === 0 ? (
                <div className={s.empty}>
                  {query ? "No sections match your search." : "No sections available."}
                </div>
              ) : (
                grouped.map(({ grade, sections: list }) => (
                  <div key={grade?.id || "unassigned"} className={s.group}>
                    <div className={s.groupHead}>{grade?.name || "Unassigned"}</div>
                    <div className={s.grid}>
                      {list.map(sec => (
                        <button
                          key={sec.id}
                          type="button"
                          className={`${s.option} ${sec.id === value ? s.optionActive : ""}`}
                          onClick={() => pick(sec.id)}>
                          {sec.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className={s.foot}>
              <span className={s.count}>
                {totalMatching} section{totalMatching === 1 ? "" : "s"}
                {value ? " · " + (currentLabel || "") : ""}
              </span>
              <div>
                {value && !required && (
                  <button type="button" className={s.btnClear} onClick={() => pick(null)} style={{ marginRight: 6 }}>
                    Clear
                  </button>
                )}
                <button type="button" className={s.btnGhost} onClick={() => setOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
